// src/pages/admin/AdminCreditsQuotasPage.jsx — Pro v2 uniforme
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Stack, IconButton, Tooltip, LinearProgress,
  InputAdornment, TextField, CircularProgress, Paper, Chip,
  Snackbar, Alert, Divider,
} from "@mui/material";
import SearchIcon        from "@mui/icons-material/Search";
import RefreshIcon       from "@mui/icons-material/Refresh";
import ArrowUpwardIcon   from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CreditCardIcon    from "@mui/icons-material/CreditCard";
import PeopleIcon        from "@mui/icons-material/People";
import ScienceIcon       from "@mui/icons-material/Science";
import TrendingUpIcon    from "@mui/icons-material/TrendingUp";
import { useTranslation } from "react-i18next";
import api, { getUserMessage } from "../../api/axios";
import { pageWrap, tableHeaderCell, tableRow, C } from "../../utils/pageStyles";

// ─── KPI card (thème clair) ──────────────────────────────────────────────────
function KpiCard({ icon, label, value, sub, accent, progress }) {
  return (
    <Box sx={{ flex:1, minWidth:200, position:"relative", overflow:"hidden",
      bgcolor:"#fff", border:"1px solid", borderColor:"divider", borderRadius:3, p:2.5 }}>
      <Box sx={{ position:"absolute", top:0, left:0, right:0, height:3,
        bgcolor: accent, borderRadius:"3px 3px 0 0" }}/>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mt:.5 }}>
        <Box sx={{ width:38, height:38, borderRadius:2, display:"flex",
          alignItems:"center", justifyContent:"center",
          bgcolor: `${accent}18`, border:`1px solid ${accent}33` }}>
          {React.cloneElement(icon, { sx:{ fontSize:20, color:accent } })}
        </Box>
        {progress!=null && (
          <Chip size="small" label={`${progress.toFixed(1)}%`}
            sx={{ height:20, fontSize:11, fontWeight:700, bgcolor:`${accent}18`, color:accent }}/>
        )}
      </Stack>
      <Typography sx={{ fontSize:28, fontWeight:800, color:"text.primary", mt:1.5, lineHeight:1 }}>
        {value}
      </Typography>
      <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mt:.5 }}>
        {label}
      </Typography>
      {sub && <Typography variant="caption" color="text.disabled" sx={{ mt:.25, display:"block" }}>{sub}</Typography>}
      {progress!=null && (
        <LinearProgress variant="determinate" value={Math.min(100,progress)}
          sx={{ mt:1.5, height:4, borderRadius:2,
            bgcolor:`${accent}18`, "& .MuiLinearProgress-bar":{ bgcolor:accent, borderRadius:2 } }}/>
      )}
    </Box>
  );
}

function SortArrow({ field, sort }) {
  if (sort.field!==field) return null;
  return sort.dir==="asc"
    ? <ArrowUpwardIcon  sx={{ fontSize:11, ml:.5, color:"#6366f1", verticalAlign:"middle" }}/>
    : <ArrowDownwardIcon sx={{ fontSize:11, ml:.5, color:"#6366f1", verticalAlign:"middle" }}/>;
}

const fmtMin = v => {
  if (!v||Number(v)===0) return "—";
  const n=Number(v); if(n<60) return `${n} min`;
  const h=Math.floor(n/60), m=n%60;
  return m>0?`${h}h ${m}m`:`${h}h`;
};
const fmtNum = v => v==null?"—":Number(v).toLocaleString();

export default function AdminCreditsQuotasPage() {
  const { t } = useTranslation();
  const [loading,    setLoading]    = useState(false);
  const [stats,      setStats]      = useState({ global:{}, by_user:[], by_lab:[] });
  const [reload,     setReload]     = useState(0);
  const [searchUser, setSearchUser] = useState("");
  const [sort,       setSort]       = useState({ field:"consumed_minutes", dir:"desc" });
  const [snack,      setSnack]      = useState(null);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const r = await api.get("/reservations/stats/admin/credits");
      setStats(r.data||{});
    } catch(e) {
      setSnack({ text: getUserMessage(e)||t("errors.load_credits_stats"), sev:"error" });
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchStats(); }, [reload]);

  const g = stats.global||{};
  const pool    = (g.total_remaining_minutes||0)+(g.total_consumed_minutes||0);
  const usagePct = pool>0 ? Math.min(100,100*(g.total_consumed_minutes||0)/pool) : 0;

  const sortedUsers = useMemo(() => {
    const arr=[...(stats.by_user||[])];
    arr.sort((a,b) => {
      const av=Number(a[sort.field]||0), bv=Number(b[sort.field]||0);
      return sort.dir==="asc"?(av-bv):(bv-av);
    });
    return arr;
  }, [stats.by_user, sort]);

  const filteredUsers = useMemo(() => {
    if (!searchUser) return sortedUsers;
    const q = searchUser.toLowerCase().trim();
    return sortedUsers.filter(u =>
      (u.username||"").toLowerCase().includes(q)||String(u.user_id).includes(q));
  }, [sortedUsers, searchUser]);

  const toggleSort = field => setSort(p => ({
    field, dir: p.field===field?(p.dir==="asc"?"desc":"asc"):"desc"
  }));

  const labsWithQuotas = useMemo(() =>
    (stats.by_lab||[]).filter(l =>
      l.max_daily_minutes_per_user>0||l.max_weekly_minutes_per_user>0||
      l.max_active_reservations_per_user>0),
    [stats.by_lab]);

  const thSx = field => ({
    ...tableHeaderCell, p:1.5, cursor:"pointer", userSelect:"none",
    color: sort.field===field ? "#6366f1" : undefined,
    "&:hover":{ color:"#6366f1" }, transition:"color .15s",
  });

  return (
    <Box sx={pageWrap}>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Stack direction={{ xs:"column", sm:"row" }} justifyContent="space-between"
        alignItems={{ sm:"center" }} spacing={2} sx={{ mb:3 }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <CreditCardIcon sx={{ fontSize:22, color:"#6366f1" }}/>
            <Typography variant="h5" fontWeight={700}>{t("admin.credits_title")}</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt:.5 }}>
            {t("admin.credits_subtitle")}
          </Typography>
        </Box>
        <Tooltip title={t("common.refresh")}>
          <IconButton onClick={()=>setReload(c=>c+1)} disabled={loading} size="small"
            sx={{ border:"1px solid", borderColor:"divider", borderRadius:2 }}>
            {loading?<CircularProgress size={16}/>:<RefreshIcon fontSize="small"/>}
          </IconButton>
        </Tooltip>
      </Stack>

      {loading && <LinearProgress sx={{ mb:2.5, borderRadius:1 }}/>}

      {/* ── KPIs ────────────────────────────────────────────────────────── */}
      <Stack direction={{ xs:"column", sm:"row" }} spacing={2} sx={{ mb:3 }}>
        <KpiCard icon={<CreditCardIcon/>} label={t("common.active_wallets")}
          value={fmtNum(g.total_wallets)} accent="#6366f1"
          sub={t("admin.credits_global_balance")}/>
        <KpiCard icon={<TrendingUpIcon/>} label={t("credits.balance")}
          value={fmtMin(g.total_remaining_minutes)} accent="#22c55e"
          sub="Minutes disponibles sur la plateforme"/>
        <KpiCard icon={<TrendingUpIcon/>} label={t("admin.minutes_consumed")}
          value={fmtMin(g.total_consumed_minutes)} accent="#f59e0b"
          progress={usagePct} sub="Taux d'utilisation global"/>
      </Stack>

      {/* ── Section Utilisateurs ────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ border:"1px solid", borderColor:"divider",
        borderRadius:3, overflow:"hidden", mb:2.5 }}>

        <Stack direction={{ xs:"column", sm:"row" }} justifyContent="space-between"
          alignItems={{ sm:"center" }} spacing={1.5}
          sx={{ px:2.5, py:2, borderBottom:"1px solid", borderColor:"divider" }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <PeopleIcon sx={{ fontSize:18, color:"#6366f1" }}/>
            <Typography variant="subtitle1" fontWeight={700}>{t("admin.users_credits")}</Typography>
            <Chip size="small" label={filteredUsers.length}
              sx={{ height:20, fontSize:11, fontWeight:700, bgcolor:"#eef2ff", color:"#6366f1" }}/>
          </Stack>
          <TextField size="small" placeholder={t("admin.filter_user")}
            value={searchUser} onChange={e=>setSearchUser(e.target.value)}
            sx={{ "& .MuiOutlinedInput-root":{ borderRadius:2 }, minWidth:240 }}
            InputProps={{ startAdornment:<InputAdornment position="start">
              <SearchIcon sx={{ fontSize:16, color:"text.disabled" }}/></InputAdornment> }}/>
        </Stack>

        {/* En-têtes */}
        <Box component="table" sx={{ width:"100%", borderCollapse:"collapse" }}>
          <Box component="thead">
            <Box component="tr" sx={{ bgcolor:"#f8fafc" }}>
              {[
                { f:"user_id",          l:"ID",                        w:70  },
                { f:"username",         l:t("admin.col_username"),     w:null},
                { f:"total_wallets",    l:"Wallets",                   w:90  },
                { f:"remaining_minutes",l:t("credits.balance"),        w:140 },
                { f:"consumed_minutes", l:t("admin.minutes_consumed"), w:150 },
                { f:null,               l:"Solde",                     w:150 },
              ].map(({f,l,w},i)=>(
                <Box component="th" key={i} onClick={()=>f&&toggleSort(f)}
                  sx={{ ...thSx(f), width:w||undefined, textAlign:i>1?"right":"left" }}>
                  {l}<SortArrow field={f} sort={sort}/>
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {filteredUsers.length===0 && (
              <Box component="tr">
                <Box component="td" colSpan={6}
                  sx={{ py:5, textAlign:"center", color:"text.secondary" }}>
                  {t("admin.no_user_found")}
                </Box>
              </Box>
            )}
            {filteredUsers.map(u => {
              const total  = Number(u.total_minutes||0);
              const remain = Number(u.remaining_minutes||0);
              const pct    = total>0 ? Math.min(100,(100*remain)/total) : 0;
              const barColor = pct>60?"#22c55e":pct>25?"#f59e0b":"#ef4444";
              return (
                <Box component="tr" key={u.user_id}
                  sx={{ ...tableRow, "& td":{ p:1.5, borderBottom:"1px solid #f1f5f9" } }}>
                  <Box component="td">
                    <Typography variant="caption" sx={{ fontFamily:"monospace", color:"text.secondary" }}>
                      #{u.user_id}
                    </Typography>
                  </Box>
                  <Box component="td">
                    <Typography variant="body2" fontWeight={600}>
                      {u.username||<span style={{color:"#cbd5e1"}}>—</span>}
                    </Typography>
                  </Box>
                  <Box component="td" sx={{ textAlign:"right" }}>
                    <Typography variant="body2" fontWeight={600}>{fmtNum(u.total_wallets)}</Typography>
                  </Box>
                  <Box component="td" sx={{ textAlign:"right" }}>
                    <Typography variant="body2" fontWeight={700}
                      sx={{ color: remain>0?"#16a34a":"#94a3b8" }}>
                      {fmtMin(u.remaining_minutes)}
                    </Typography>
                  </Box>
                  <Box component="td" sx={{ textAlign:"right" }}>
                    <Typography variant="body2" fontWeight={600} sx={{ color:"#d97706" }}>
                      {fmtMin(u.consumed_minutes)}
                    </Typography>
                  </Box>
                  <Box component="td" sx={{ pl:1 }}>
                    <LinearProgress variant="determinate" value={pct}
                      sx={{ height:5, borderRadius:3, bgcolor:`${barColor}18`,
                        "& .MuiLinearProgress-bar":{ bgcolor:barColor, borderRadius:3 } }}/>
                    <Typography variant="caption" color="text.disabled"
                      sx={{ display:"block", textAlign:"right", mt:.25 }}>
                      {pct.toFixed(0)}%
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Paper>

      {/* ── Section Labs / Quotas ────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ border:"1px solid", borderColor:"divider",
        borderRadius:3, overflow:"hidden" }}>

        <Stack direction={{ xs:"column", sm:"row" }} justifyContent="space-between"
          alignItems={{ sm:"center" }} spacing={1.5}
          sx={{ px:2.5, py:2, borderBottom:"1px solid", borderColor:"divider" }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <ScienceIcon sx={{ fontSize:18, color:"#f59e0b" }}/>
            <Typography variant="subtitle1" fontWeight={700}>{t("admin.quotas_per_lab")}</Typography>
            <Chip size="small" label={(stats.by_lab||[]).length}
              sx={{ height:20, fontSize:11, fontWeight:700, bgcolor:"#fffbeb", color:"#b45309" }}/>
          </Stack>
          {labsWithQuotas.length>0 && (
            <Typography variant="caption" color="text.secondary">
              {labsWithQuotas.length} lab{labsWithQuotas.length!==1?"s":""} avec quotas définis
            </Typography>
          )}
        </Stack>

        <Box component="table" sx={{ width:"100%", borderCollapse:"collapse" }}>
          <Box component="thead">
            <Box component="tr" sx={{ bgcolor:"#f8fafc" }}>
              {[
                { l:"ID",                                              w:60  },
                { l:t("labs.name"),                                    w:null},
                { l:t("admin.col_capacity"),                           w:90  },
                { l:t("adminLabRules.columns.dailyMinutesPerUser"),    w:130 },
                { l:t("adminLabRules.columns.weeklyMinutesPerUser"),   w:140 },
                { l:t("admin.max_reservations_per_user"),              w:130 },
              ].map(({l,w},i)=>(
                <Box component="th" key={i}
                  sx={{ ...tableHeaderCell, p:1.5, width:w||undefined,
                    textAlign:i>1?"right":"left" }}>{l}</Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {(stats.by_lab||[]).length===0 && (
              <Box component="tr">
                <Box component="td" colSpan={6}
                  sx={{ py:5, textAlign:"center", color:"text.secondary" }}>
                  {t("admin.no_lab_found")}
                </Box>
              </Box>
            )}
            {(stats.by_lab||[]).map(lab => {
              const hasQ = (lab.max_daily_minutes_per_user||0)>0||
                (lab.max_weekly_minutes_per_user||0)>0||
                (lab.max_active_reservations_per_user||0)>0;
              return (
                <Box component="tr" key={lab.lab_id}
                  sx={{ ...tableRow, "& td":{ p:1.5, borderBottom:"1px solid #f1f5f9" } }}>
                  <Box component="td">
                    <Typography variant="caption" sx={{ fontFamily:"monospace", color:"text.secondary" }}>
                      #{lab.lab_id}
                    </Typography>
                  </Box>
                  <Box component="td">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" fontWeight={600}>{lab.lab_name}</Typography>
                      {hasQ && (
                        <Chip size="small" label="Quotas"
                          sx={{ height:16, fontSize:10, fontWeight:700,
                            bgcolor:"#fffbeb", color:"#b45309" }}/>
                      )}
                    </Stack>
                  </Box>
                  <Box component="td" sx={{ textAlign:"right" }}>
                    <Typography variant="body2" fontWeight={600}>
                      {lab.capacity??<span style={{color:"#cbd5e1"}}>—</span>}
                    </Typography>
                  </Box>
                  <Box component="td" sx={{ textAlign:"right" }}>
                    {lab.max_daily_minutes_per_user>0
                      ? <Typography variant="body2" fontWeight={600} sx={{ color:"#d97706" }}>
                          {fmtMin(lab.max_daily_minutes_per_user)}
                        </Typography>
                      : <Typography variant="caption" color="text.disabled">—</Typography>}
                  </Box>
                  <Box component="td" sx={{ textAlign:"right" }}>
                    {lab.max_weekly_minutes_per_user>0
                      ? <Typography variant="body2" fontWeight={600} sx={{ color:"#6366f1" }}>
                          {fmtMin(lab.max_weekly_minutes_per_user)}
                        </Typography>
                      : <Typography variant="caption" color="text.disabled">—</Typography>}
                  </Box>
                  <Box component="td" sx={{ textAlign:"right" }}>
                    {lab.max_active_reservations_per_user>0
                      ? <Typography variant="body2" fontWeight={600} sx={{ color:"#0284c7" }}>
                          {lab.max_active_reservations_per_user}
                        </Typography>
                      : <Typography variant="caption" color="text.disabled">—</Typography>}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Paper>

      <Snackbar open={!!snack} autoHideDuration={5000}
        onClose={()=>setSnack(null)} anchorOrigin={{vertical:"bottom",horizontal:"center"}}>
        <Alert severity={snack?.sev||"error"} onClose={()=>setSnack(null)}
          sx={{ borderRadius:2 }}>{snack?.text}</Alert>
      </Snackbar>
    </Box>
  );
}
